import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Wand2, Loader2, Check, X, RotateCcw, Sparkles, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRoomFavorites } from './useRoomFavorites';

const API = process.env.REACT_APP_BACKEND_URL;

export const ROOM_OPTIONS = [
  // Luxury Residential
  { value: 'penthouse_living', label: 'Penthouse Living', category: 'Residential' },
  { value: 'master_suite', label: 'Master Suite', category: 'Residential' },
  { value: 'designer_kitchen', label: 'Designer Kitchen', category: 'Residential' },
  { value: 'home_library', label: 'Home Library', category: 'Residential' },
  { value: 'breakfast_nook', label: 'Breakfast Nook', category: 'Residential' },
  { value: 'corner_living', label: 'Corner Living Room', category: 'Residential', badge: 'corner' },
  { value: 'loft_apartment', label: 'Urban Loft', category: 'Residential', badge: 'angled' },
  { value: 'corner_bedroom', label: 'Corner Bedroom', category: 'Residential', badge: 'corner' },
  { value: 'modern_nursery', label: 'Modern Nursery', category: 'Residential', badge: 'angled' },
  { value: 'master_bathroom', label: 'Spa Bathroom', category: 'Residential', badge: 'angled' },
  { value: 'sunroom', label: 'Glass Sunroom', category: 'Residential', badge: 'corner' },
  // Commercial / Hospitality
  { value: 'boutique_hotel', label: 'Boutique Hotel', category: 'Commercial' },
  { value: 'executive_office', label: 'Executive Office', category: 'Commercial', badge: 'corner' },
  { value: 'fine_dining', label: 'Fine Dining', category: 'Commercial', badge: 'angled' },
  { value: 'wellness_spa', label: 'Wellness Spa', category: 'Commercial' },
  { value: 'coworking_loft', label: 'Co-Working Loft', category: 'Commercial' },
  { value: 'conference_room', label: 'Conference Room', category: 'Commercial', badge: 'corner' },
  { value: 'retail_showroom', label: 'Retail Showroom', category: 'Commercial', badge: 'angled' },
  { value: 'art_gallery', label: 'Art Gallery', category: 'Commercial', badge: 'angled' },
  { value: 'yoga_studio', label: 'Yoga Studio', category: 'Commercial', badge: 'corner' },
  // Custom
  { value: 'custom', label: 'Custom Scene', category: 'Custom' },
];

const STYLE_PRESETS = [
  'Warm golden hour light, luxury editorial mood',
  'Cool morning light, crisp Scandinavian style',
  'Dramatic evening ambiance, city lights visible',
  'Bright natural daylight, clean minimal aesthetic',
  'Moody overcast day, rich material textures',
];

export const AiRegenerateButton = ({ selectedColor, currentVisualization, onVisualizationUpdated, fold = null }) => {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user?.is_admin) return null;
  if (!selectedColor) return null;

  return (
    <>
      <button
        data-testid="ai-regenerate-btn"
        onClick={() => setOpen(true)}
        className="absolute top-3 right-3 z-30 flex items-center gap-1.5 px-2.5 py-1.5
                   bg-black/70 hover:bg-black/90 text-white text-xs font-medium
                   rounded-md backdrop-blur-sm transition-all duration-200
                   opacity-0 group-hover:opacity-100 pointer-events-auto"
        title="AI Regenerate (Admin)"
      >
        <Wand2 size={13} />
        <span>AI Regen</span>
      </button>

      {open && createPortal(
        <AiRegenerateModal
          selectedColor={selectedColor}
          currentVisualization={currentVisualization}
          token={token}
          onClose={() => setOpen(false)}
          onVisualizationUpdated={onVisualizationUpdated}
          fold={fold}
        />,
        document.body
      )}
    </>
  );
};

const AiRegenerateModal = ({ selectedColor, currentVisualization, token, onClose, onVisualizationUpdated, fold }) => {
  const [roomType, setRoomType] = useState('penthouse_living');
  const [customScene, setCustomScene] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null); // { id, preview_url, previous_url }
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [makeDefault, setMakeDefault] = useState(true);
  const { favorites, loaded, toggle } = useRoomFavorites(token);
  useEffect(() => { if (loaded && favorites.length) setRoomType(favorites[0]); }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const roomsFor = (cat) => cat === 'Favourites'
    ? ROOM_OPTIONS.filter(o => favorites.includes(o.value))
    : ROOM_OPTIONS.filter(o => o.category === cat && !favorites.includes(o.value));

  const currentUrl = currentVisualization?.image_url
    || (selectedColor?.room_visualizations?.living_room)
    || selectedColor?.room_visualization_url;

  const absUrl = (u) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    return `${API}${u.startsWith('/') ? '' : '/'}${u}`;
  };

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    setPreview(null);
    try {
      const res = await fetch(`${API}/api/admin/fabrics/visualizations/inline-regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          color_id: selectedColor.id,
          room_type: roomType === 'custom' ? 'penthouse_living' : roomType,
          custom_prompt: customPrompt || null,
          custom_scene: roomType === 'custom' ? customScene : null,
          fold: fold || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Generation failed (${res.status})`);
      }
      const data = await res.json();
      setPreview(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  }, [selectedColor, roomType, customPrompt, token, fold]);

  const handleApprove = useCallback(async () => {
    if (!preview) return;
    setApproving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/fabrics/visualizations/inline-regenerate/${preview.id}/approve?make_default=${makeDefault}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Approve failed');
      setApproved(true);
      // Pass the FULL response (live_url + shade_regions) so the parent can
      // rebuild the compositing pipeline without a page reload.
      onVisualizationUpdated?.({
        url: data.live_url || preview.preview_url,
        shade_regions: data.shade_regions || null,
        room_type: preview.room_type || 'living_room',
        make_default: makeDefault,
        fold: preview.fold || null,
      });
      // Auto-close dialog after brief success display
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setApproving(false);
    }
  }, [preview, token, onVisualizationUpdated, onClose, makeDefault]);

  const handleDiscard = useCallback(async () => {
    if (!preview) return;
    try {
      await fetch(`${API}/api/admin/fabrics/visualizations/inline-regenerate/${preview.id}/discard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* silent */ }
    setPreview(null);
  }, [preview, token]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl mx-4 bg-[#1a1a1f] border border-white/10 rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Sparkles size={16} className="text-amber-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-sm">AI Scene Regeneration</h2>
              <p className="text-white/50 text-xs mt-0.5">
                {selectedColor?.name} &middot; {selectedColor?.code}
                {fold && <span className="ml-1 text-amber-300" data-testid="ai-regen-fold">&middot; {fold.charAt(0).toUpperCase() + fold.slice(1)} roman fold</span>}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="ai-regen-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Scene Selection */}
          <div className="space-y-4">
            {/* Scene Type Selector — grouped by category */}
            <div>
              <label className="block text-xs font-medium text-white/60 mb-2">Scene</label>
              {['Favourites', 'Residential', 'Commercial', 'Custom'].filter(cat => roomsFor(cat).length).map(cat => (
                <div key={cat} className="mb-2">
                  <p className="text-[10px] uppercase tracking-wider text-white/30 mb-1.5">{cat}</p>
                  <div className="flex flex-wrap gap-1.5" data-testid={`ai-regen-scenes-${cat.toLowerCase()}`}>
                    {roomsFor(cat).map(o => (
                      <button
                        key={o.value}
                        data-testid={`ai-regen-room-${o.value}`}
                        type="button"
                        onClick={() => !generating && !preview && setRoomType(o.value)}
                        disabled={generating || !!preview}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border inline-flex items-center gap-1
                          ${roomType === o.value
                            ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                            : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'}
                          disabled:opacity-40 disabled:pointer-events-none`}
                      >
                        {o.label}
                        {o.badge && (
                          <span className={`text-[8px] uppercase font-bold px-1 py-px rounded ${
                            o.badge === 'corner' ? 'bg-blue-500/30 text-blue-300' : 'bg-violet-500/30 text-violet-300'
                          }`}>{o.badge}</span>
                        )}
                        {o.value !== 'custom' && (
                          <span
                            role="button"
                            tabIndex={0}
                            title={favorites.includes(o.value) ? 'Remove from favourites' : 'Add to favourites'}
                            onClick={(e) => { e.stopPropagation(); toggle(o.value); }}
                            data-testid={`ai-regen-fav-${o.value}`}
                            className="pointer-events-auto"
                          >
                            <Star size={11} className={favorites.includes(o.value) ? 'fill-amber-400 text-amber-400' : 'text-white/25 hover:text-amber-300'} />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Custom Scene Description — shown only when "Custom Scene" is selected */}
              {roomType === 'custom' && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Describe your scene</label>
                  <textarea
                    data-testid="ai-regen-custom-scene"
                    value={customScene}
                    onChange={e => setCustomScene(e.target.value)}
                    disabled={generating || !!preview}
                    placeholder="e.g., Luxury yacht master cabin with panoramic ocean-view windows, teak interior..."
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/25
                               focus:outline-none focus:border-amber-500/50 resize-none disabled:opacity-50"
                  />
                </div>
              )}
            </div>

            {/* Fabric Info (read-only) */}
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">Fabric</label>
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                {selectedColor?.image_url && (
                  <img src={absUrl(selectedColor.image_url)} alt="" className="w-6 h-6 rounded object-cover" />
                )}
                <span className="text-white text-sm truncate">{selectedColor?.name}</span>
                {selectedColor?.hex_color && (
                  <span className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: selectedColor.hex_color }} />
                )}
                <span className="text-white/30 text-xs ml-auto">{selectedColor?.openness || selectedColor?.fabric_type || (fold ? 'Roman Shade' : 'Roller Shade')}</span>
              </div>
            </div>
          </div>

          {/* Custom Prompt */}
          <div>
            <label className="block text-xs font-medium text-white/60 mb-1.5">Style Direction (optional)</label>
            <textarea
              data-testid="ai-regen-prompt"
              value={customPrompt}
              onChange={e => setCustomPrompt(e.target.value)}
              disabled={generating || !!preview}
              rows={2}
              placeholder="e.g. More realistic NYC living room, warmer interior, keep shade/window geometry unchanged"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm
                         placeholder:text-white/25 focus:outline-none focus:border-amber-500/50 resize-none disabled:opacity-50"
            />
            {/* Quick presets */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {STYLE_PRESETS.map(p => (
                <button
                  key={p}
                  onClick={() => setCustomPrompt(p)}
                  disabled={generating || !!preview}
                  className="text-[10px] px-2 py-1 rounded-full bg-white/5 text-white/40 hover:text-white/70 hover:bg-white/10
                             transition-colors disabled:opacity-40 border border-white/5"
                >
                  {p.length > 40 ? p.slice(0, 38) + '...' : p}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 text-red-400 text-sm" data-testid="ai-regen-error">
              {error}
            </div>
          )}

          {/* Preview Comparison */}
          {preview && !approved && (
            <div className="space-y-3">
              <p className="text-xs text-white/50 uppercase tracking-wider font-medium">Preview vs Current</p>
              <div className="grid grid-cols-2 gap-3" data-testid="ai-regen-preview-compare">
                <div className="relative rounded-lg overflow-hidden border border-white/10">
                  <img src={absUrl(currentUrl)} alt="Current" className="w-full aspect-square object-cover" />
                  <span className="absolute top-2 left-2 bg-black/70 text-white/80 text-[10px] px-2 py-0.5 rounded-full">Current</span>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-amber-500/30">
                  <img src={absUrl(preview.preview_url)} alt="Preview" className="w-full aspect-square object-cover" />
                  <span className="absolute top-2 left-2 bg-amber-500/80 text-black text-[10px] font-semibold px-2 py-0.5 rounded-full">Preview (New)</span>
                </div>
              </div>
            </div>
          )}

          {/* Approved State */}
          {approved && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center" data-testid="ai-regen-approved">
              <Check size={24} className="mx-auto text-emerald-400 mb-2" />
              <p className="text-emerald-400 text-sm font-medium">Visualization replaced successfully</p>
              <p className="text-white/40 text-xs mt-1">Previous image backed up. The preview has been updated.</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-5 border-t border-white/10">
          {!preview && !approved && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-white/50 hover:text-white transition-colors">
                Cancel
              </button>
              <button
                data-testid="ai-regen-generate-btn"
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm
                           rounded-lg transition-colors disabled:opacity-50 disabled:cursor-wait"
              >
                {generating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    Generate Preview
                  </>
                )}
              </button>
            </>
          )}

          {preview && !approved && (
            <>
              <button
                data-testid="ai-regen-discard-btn"
                onClick={handleDiscard}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white/50 hover:text-red-400 transition-colors"
              >
                <X size={14} />
                Discard
              </button>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-white/70 mr-2 cursor-pointer select-none">
                  <input type="checkbox" checked={makeDefault} onChange={e => setMakeDefault(e.target.checked)} className="accent-emerald-500" data-testid="ai-regen-make-default" />
                  Make this the default
                </label>
                <button
                  onClick={() => { setPreview(null); setError(null); }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-white/50 hover:text-white border border-white/10 rounded-lg transition-colors"
                >
                  <RotateCcw size={13} />
                  Retry
                </button>
                <button
                  data-testid="ai-regen-approve-btn"
                  onClick={handleApprove}
                  disabled={approving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-sm
                             rounded-lg transition-colors disabled:opacity-50"
                >
                  {approving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Check size={14} />
                  )}
                  {approving ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </>
          )}

          {approved && (
            <div className="w-full flex justify-end">
              <button onClick={onClose} className="px-5 py-2 text-sm bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiRegenerateButton;
